<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <base-material-card
          color="primary"
          icon="mdi-newspaper-variant"
          inline
          class="ml-0 px-5 py-4"
          title="Gerenciar Releases e Novidades"
        >
          <v-btn
            color="accent"
            dark
            elevation="0"
            class="mt-5"
            style="float: left"
            outlined
            medium
            @click="$router.push('editar')"
          >
            <v-icon left size="30px">mdi-plus-circle</v-icon>
            Nova Release
          </v-btn>

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            class="ml-auto mt-4"
            label="Procurar"
            hide-details
            single-line
            style="max-width: 250px"
          />

          <v-divider class="mt-10" />

          <v-data-table
            :headers="headers"
            :items="releases"
            :search.sync="search"
            :headerProps="{ sortByText: 'Ordenar Por' }"
            :footer-props="{ itemsPerPageText: 'Itens por Página' }"
            :mobile-breakpoint="800"
            :loading="loading"
            :hide-default-header="loading"
            :hide-default-footer="loading"
            no-results-text="Nenhum registro correspondente encontrado"
          >
            <template v-slot:[`item.status`]="{ item }">
              <v-chip :color="item.status === 'publicado' ? 'success' : 'grey'" small dark>
                {{ item.status === 'publicado' ? 'Publicado' : 'Rascunho' }}
              </v-chip>
            </template>

            <template v-slot:[`item.empresas`]="{ item }">
              <span v-if="item.empresas.length === 0" class="caption text--secondary">Todas</span>
              <span v-else>{{ item.empresas.map((e) => e.acronym).join(', ') }}</span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn color="gray" min-width="0" class="ml-n1 px-1" fab icon x-small @click="$router.push(`editar/${item.id}`)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="white" class="ml-1" height="22px" width="22px" fab x-small elevation="1" v-bind="attrs" v-on="on">
                    <v-icon small>mdi mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-if="item.status === 'rascunho'" @click="publicar(item)">
                    <v-list-item-title>
                      <v-icon small>mdi-publish</v-icon>
                      Publicar
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-else @click="despublicar(item)">
                    <v-list-item-title>
                      <v-icon small>mdi-publish-off</v-icon>
                      Despublicar
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openDialogDelete(item)">
                    <v-list-item-title>
                      <v-icon small color="red">mdi-delete</v-icon>
                      <span class="red--text">Excluir</span>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:[`footer.page-text`]>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="primary" v-bind="attrs" v-on="on" @click="getReleases()">mdi-refresh</v-icon>
                </template>
                <span>Clique aqui para recarregar as releases</span>
              </v-tooltip>
            </template>
          </v-data-table>

          <dialog-delete :dialog="dialogDelete" @update:dialog="dialogDelete = $event" @submitTriggered="deletar()">
            <template slot="title">Excluir release v{{ dialogDeleteItem.versao }}</template>
            <template slot="body">
              Tem certeza que deseja excluir a release
              <strong>v{{ dialogDeleteItem.versao }}</strong>
              ? Esta ação não pode ser desfeita.
            </template>
          </dialog-delete>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import ReleasesNovidadesService from '@/services/ReleasesNovidadesService';

  export default {
    name: 'ReleasesNovidadesGerenciar',

    components: {
      DialogDelete: () => import('@/components/general/DialogDelete.vue')
    },

    data: () => ({
      releases: [],
      search: '',
      loading: false,
      dialogDelete: false,
      dialogDeleteItem: { versao: '' },
      headers: [
        { text: 'Versão', value: 'versao' },
        { text: 'Título', value: 'titulo' },
        { text: 'Data', value: 'data_lancamento' },
        { text: 'Status', value: 'status' },
        { text: 'Itens', value: 'total_itens' },
        { text: 'Empresas', value: 'empresas', sortable: false },
        { text: 'Ações', value: 'actions', sortable: false }
      ]
    }),

    mounted() {
      this.getReleases();
    },

    methods: {
      getReleases() {
        this.loading = true;
        ReleasesNovidadesService.getReleasesAdmin()
          .then(({ data }) => (this.releases = data.data))
          .catch(() => this.$toast.error('Erro ao carregar as releases.', '', { position: 'topRight' }))
          .finally(() => (this.loading = false));
      },

      publicar(item) {
        ReleasesNovidadesService.publicar(item.id)
          .then(() => {
            item.status = 'publicado';
            this.$toast.success(`Release v${item.versao} publicada.`, '', { position: 'topRight' });
          })
          .catch(() => this.$toast.error('Erro ao publicar.', '', { position: 'topRight' }));
      },

      despublicar(item) {
        ReleasesNovidadesService.despublicar(item.id)
          .then(() => {
            item.status = 'rascunho';
            this.$toast.success(`Release v${item.versao} despublicada.`, '', { position: 'topRight' });
          })
          .catch(() => this.$toast.error('Erro ao despublicar.', '', { position: 'topRight' }));
      },

      openDialogDelete(item) {
        this.dialogDeleteItem = item;
        this.dialogDelete = true;
      },

      deletar() {
        this.dialogDelete = false;
        ReleasesNovidadesService.deletar(this.dialogDeleteItem.id)
          .then(() => {
            this.releases = this.releases.filter((r) => r.id !== this.dialogDeleteItem.id);
            this.$toast.success(`Release v${this.dialogDeleteItem.versao} excluída.`, '', { position: 'topRight' });
          })
          .catch(() => this.$toast.error('Erro ao excluir.', '', { position: 'topRight' }));
      }
    }
  };
</script>
